import gsap from "gsap";
import {
    CanvasTexture,
    MeshBasicMaterial,
    TextureLoader,
    Vector2,
    VideoTexture,
    WebGLRenderTarget,
} from "three";
import { THREE_GetGifTexture } from "threejs-gif-texture";
import { EXRLoader, GLTFLoader } from "three/examples/jsm/Addons.js";

import { MainCamera } from "./MainCamera.js";
import { MainScene } from "./MainScene.js";
import { Renderer } from "./Renderer.js";
import { State } from "./State.js";
import { DEVICE, EVENTS } from "./constants.js";
import { DeviceMotion } from "./utils/DeviceMotion.js";
import { MouseComputation } from "./utils/MouseComputation.js";
import { Noise } from "./utils/Noise.js";
import { globalUniforms, viewportUniforms } from "./utils/uniforms.js";
import Disclaimer from "./Disclaimer.js";
import Hls from "hls.js";

let dt = 0;

const NODE_ENV = process.env.NODE_ENV;

const LISA_BASE_PATH = "/assets/lisa/sixty/";

const IDLE_FILES = [
    // Loop 1
    [
        "https://stream.mux.com/aCO5naKzdS662xMPN2NjH9ZW9ZdeL0100i301tF024QPWyg.m3u8",
        "video",
    ],
    // Loop 2
    [
        "https://stream.mux.com/5Q3pTwEulaTwVGHEHscqcNiczpzKtiZaYDbjd02xHKw00.m3u8",
        "video",
    ],
    // Loop 3
    [
        "https://stream.mux.com/SlDvk8aCjdGxd00Kkb02U01YSSVSU1agBMlwUpDExfOGH00.m3u8",
        "video",
    ],

    // DEBUG
    // ['https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExOTc1aHVmMHQ1cTMyMzZmZGZqdXd3NWcxOXVlNDVpMHIwb3B6endsMCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/1Laym1dOTt2mSxIwdc/giphy.gif', 'gif']
];

class LisaGL {
    constructor() {
        this.state = new State(EVENTS);
        this.viewport = {
            width: 375,
            height: 610,
            ratio: 1,
            dpr: 1,
            breakpoint: "mobile",
        };
        this.assets = {};

        this.searchParams = new URLSearchParams(window.location.search);
        this.debug =
            NODE_ENV === "development" && this.searchParams.has("debug");
        this.state.register(this);

        this.mouse = new Vector2(0, 0);
        this.noise = new Noise();
        this.playingAnimation = false;

        this.textureLoader = new TextureLoader();
        this.hlsInstance = null; // Single reusable HLS instance
    }

    async init(wrapper, params = {}) {
        this.$wrapper = wrapper;
        this.params = params;

        if (!this.initialized) {
            this.renderer = new Renderer();
            this.scene = new MainScene();
            this.camera = new MainCamera();

            this.mouseComputation = new MouseComputation();
            this.deviceMotion = new DeviceMotion();

            await this.load(LISA_BASE_PATH);
        } else {
            this.camera.reset();
        }

        this.$wrapper.prepend(this.renderer.domElement);
        this.initialized = true;

        if (this.showDisclaimer) this.setDisclaimer();
    }

    destroy() {
        // Kill all GSAP timelines
        this.yesTl?.kill();
        this.noTl?.kill();
        this.hesitateTl?.kill();
        this.moveBackTl?.kill();
        this.idleTw?.kill();

        // Destroy single HLS instance
        if (this.hlsInstance) {
            try {
                this.hlsInstance.destroy();
                this.hlsInstance = null;
            } catch (e) {
                console.warn("Error destroying HLS instance:", e);
            }
        }

        // Clean up disclaimer texture if it exists
        if (this.disclaimer?.texture) {
            this.disclaimer.texture.dispose();
        }

        this.$wrapper.removeChild(this.renderer.domElement);
    }

    async load(baseUrl) {
        const gltfLoader = new GLTFLoader();
        const exrLoader = new EXRLoader();
        const [model, envmap] = await Promise.all([
            new Promise((resolve) => {
                gltfLoader.load(`${baseUrl}lisa.glb`, (model) =>
                    resolve(model)
                );
            }),
            new Promise(async (resolve) => {
                exrLoader.load(`${baseUrl}envmap.exr`, (envmap) =>
                    resolve(envmap)
                );
            }),
        ]);

        this.assets.model = model;
        this.assets.envmap = envmap;

        this.state.emit(EVENTS.APP_LOADED);
        this.state.emit(EVENTS.ATTACH);
    }

    tick(et, reset = false) {
        et /= 1000;
        dt = reset ? 0 : Math.min(et - globalUniforms.uTime.value, 0.01);
        globalUniforms.uTime.value = et;
        lisa.state.emit(EVENTS.TICK, { et: et, dt: dt });
        this.#render();
        lisa.state.emit(EVENTS.RENDER);

        this.disclaimer?.render(dt);
    }

    mousemove(x, y) {
        this.mouse.x = x * 2 - 1;
        this.mouse.y = -y * 2 + 1;

        globalUniforms.uMouse.value.x = this.mouse.x;
        globalUniforms.uMouse.value.y = this.mouse.y;
        lisa.state.emit(EVENTS.POINTER_MOVE, this.mouse);
    }

    click() {
        if (this.mouse.length() <= 0.5) {
            this.moveBack();
        }
    }

    resize(width, height) {
        this.viewport.width = width;
        this.viewport.height = height;
        this.viewport.ratio = width / height;
        this.viewport.dpr = 1;
        this.viewport.breakpoint =
            window.innerWidth < DEVICE.BREAKPOINT ? "mobile" : "desktop";
        viewportUniforms.uMobile.value = lisa.viewport.breakpoint == "mobile";

        viewportUniforms.uResolution.value.set(
            width * this.viewport.dpr,
            height * this.viewport.dpr
        );
        viewportUniforms.uRatio.value = this.viewport.ratio;
        lisa.state.emit(EVENTS.RESIZE, this.viewport);
    }

    sayYes() {
        this.yesTl?.kill();
        this.yesTl = gsap.timeline();

        const duration = 0.2;
        const angle = Math.PI * 0.015;

        const target = this.scene.lisa.additiveRotations.neck2;

        this.yesTl.to(target, {
            x: angle * 0.5,
            duration,
            ease: "power1.inOut",
        });
        this.yesTl.to(target, { x: -angle, duration, ease: "power1.inOut" });
        this.yesTl.to(target, {
            x: angle * 0.5,
            duration,
            ease: "power1.inOut",
        });
        this.yesTl.to(target, { x: 0, duration, ease: "power1.inOut" });
    }
    sayNo() {
        this.noTl?.kill();
        this.noTl = gsap.timeline();

        const duration = 0.2;
        const angle = Math.PI * 0.005;

        const target = this.scene.lisa.additiveRotations.neck1;

        this.noTl.to(target, {
            y: angle * 0.3,
            duration,
            ease: "power1.inOut",
        });
        this.noTl.to(target, { y: -angle, duration, ease: "power1.inOut" });
        this.noTl.to(target, { y: angle, duration, ease: "power1.inOut" });
        this.noTl.to(target, {
            y: -angle * 0.3,
            duration,
            ease: "power1.inOut",
        });
        this.noTl.to(target, { y: 0, duration, ease: "power1.inOut" });
    }
    hesitate() {
        this.hesitateTl?.kill();
        this.hesitateTl = gsap.timeline();

        const angle = Math.PI * 0.05;

        const target = this.scene.lisa.additiveRotations.neck2;

        this.hesitateTl.to(
            target,
            { z: angle, duration: 1, ease: "power2.inOut" },
            0
        );
        this.hesitateTl.to(
            target,
            { x: 0.02, duration: 2, ease: "power1.inOut" },
            1
        );
        this.hesitateTl.to(
            target,
            { z: 0, duration: 1, ease: "power2.inOut" },
            2
        );
        this.hesitateTl.to(
            target,
            { x: 0, duration: 1, ease: "power2.inOut" },
            3
        );
    }
    moveBack() {
        this.moveBackTl?.kill();
        this.moveBackTl = gsap.timeline();

        const torso = this.scene.lisa.additiveRotations.torso;
        const neck1 = this.scene.lisa.additiveRotations.neck1;

        this.moveBackTl.to(
            torso,
            { x: -0.4, duration: 0.5, ease: "power2.out" },
            0
        );
        this.moveBackTl.to(
            neck1,
            { x: 0.3, duration: 0.3, ease: "power2.out" },
            0
        );
        this.moveBackTl.to(
            torso,
            { x: 0, duration: 2, ease: "power1.inOut" },
            1.2
        );
        this.moveBackTl.to(
            neck1,
            { x: 0, duration: 1, ease: "power1.inOut" },
            1.4
        );
    }

    get screenSwitchedOn() {
        if (this.scene.lisa) return this.scene.lisa.screen.material.switchedOn;
        return 0;
    }
    set screenSwitchedOn(value) {
        this.scene.lisa.screen.material.switchedOn = value;
    }

    get screenGlitchIntensity() {
        if (this.scene.lisa)
            return this.scene.lisa.screen.material.glitchIntensity;
        return 0;
    }
    set screenGlitchIntensity(value) {
        this.scene.lisa.screen.material.glitchIntensity = value;
    }

    get screenGlitchFrequency() {
        if (this.scene.lisa) return this.scene.lisa.screen.material.timeScale;
        return 0;
    }
    set screenGlitchFrequency(value) {
        this.scene.lisa.screen.material.timeScale = value;
    }

    get displayRunningCode() {
        if (this.scene.lisa) return this.scene.lisa.screen.material.displayCode;
        return 0;
    }
    set displayRunningCode(value) {
        this.scene.lisa.screen.material.displayCode = value;
    }
    get speechVolume() {
        if (this.scene.lisa)
            return this.scene.lisa.lights[0].material.speechVolume;
        return 0;
    }
    set speechVolume(value) {
        this.scene.lisa.lights.forEach((child) => {
            if (child.material) child.material.speechVolume = value;
        });
    }

    get xDecay() {
        return this.scene.position.x;
    }
    set xDecay(value) {
        this.scene.position.x = value;
    }

    get darkMode() {
        return globalUniforms.uDarkMode.value;
    }
    set darkMode(value) {
        globalUniforms.uDarkMode.value = value;
    }
    // get leftSideContent() {
    // 	if (this.scene.lisa) return this.scene.lisa.screen.material.leftSide;
    // 	return 0;
    // }
    // set leftSideContent(value) {
    // 	this.scene.lisa.screen.material.leftSide.value = value;
    // }
    // get rightSideContent() {
    // 	if (this.scene.lisa) return this.scene.lisa.screen.material.rightSide;
    // 	return 0;
    // }
    // set rightSideContent(value) {
    // 	this.scene.lisa.screen.material.rightSide = value;
    // }

    setContent = (url, type = "image", onComplete = undefined) => {
        this.setLeftSideContent(url, type, onComplete);
        this.setRightSideContent(url, type, onComplete);
    };

    setLeftSideContent = (url, type = "image", onComplete = undefined) => {
        this.setSideContent(url, "left", type, onComplete);
    };

    setRightSideContent = (url, type = "image", onComplete = undefined) => {
        this.setSideContent(url, "right", type, onComplete);
    };

    setSideContent = async (
        url,
        side,
        type = "image",
        onComplete = undefined
    ) => {
        clearTimeout(this.onCompleteTO);
        this.idleTw?.kill?.();

        // Store old texture for disposal
        const oldTexture = this.scene.lisa.screen.material[`${side}Side`];

        let texture = null;
        if (type === "video") {
            texture = new VideoTexture(this.createVideo(url, onComplete));
        } else if (type === "gif") {
            texture = await THREE_GetGifTexture(url);
            // Ensure GIF textures are properly tracked for disposal
        } else {
            texture = await this.textureLoader.load(url);
        }

        if (onComplete && type !== "video") {
            this.onCompleteTO = setTimeout(onComplete, 3500);
        }

        this.setSideTexture(texture, side);

        // Dispose old texture if it's different and not a video texture
        // (video textures are handled in setSideTexture)
        if (
            oldTexture &&
            !oldTexture.isVideoTexture &&
            oldTexture !== texture
        ) {
            oldTexture.dispose();
        }

        if (this.disclaimer) {
            if (this.disclaimer.texture) {
                this.disclaimer.texture.dispose();
            }
            this.disclaimer = null;
        }
    };

    setSideTexture(texture, side) {
        const oldTexture = this.scene.lisa.screen.material[`${side}Side`];

        if (oldTexture.isVideoTexture) {
            const video = oldTexture.source.data;

            // Detach HLS from old video if it exists
            if (this.hlsInstance && this.hlsInstance.media === video) {
                try {
                    this.hlsInstance.detachMedia();
                } catch (e) {
                    console.warn("Error detaching HLS from video:", e);
                }
            }

            // Clean up video element
            video.pause();
            video.removeAttribute("src");
            video.src = "";
            video.load();

            // Remove event listeners
            video.onended = null;
            video.onerror = null;
        }

        this.scene.lisa.screen.material[`${side}Side`] = texture;

        if (oldTexture.isTexture) {
            oldTexture.dispose();
        }
    }

    setIdle() {
        this.setContent(
            ...IDLE_FILES[Math.floor(Math.random() * IDLE_FILES.length)]
        );

        this.idleTw = gsap.to(lisa, {
            screenGlitchFrequency: 0.05,
            screenGlitchIntensity: 0,
            displayRunningCode: 0,
            delay: 0.5,
        });
    }

    setDisclaimer(locale = 'en') {
        if (!this.initialized) {
            this.showDisclaimer = true;
            return;
        }

        const disclaimerText = {
            en: '[CLICK] TO START',
            fr: '[CLIQUEZ] POUR COMMENCER'
        };
        this.disclaimer = new Disclaimer(disclaimerText[locale]);
        for (const side of ["left", "right"])
            this.setSideTexture(this.disclaimer.texture, side);
    }

    createVideo = (src, onComplete = undefined) => {
        const video = document.createElement("video");
        video.src = src;
        video.crossOrigin = "anonymous";
        video.preload = "auto";
        video.type = "video/mp4";
        video.loop = !onComplete;
        video.muted = true;

        if (video.src.includes(".m3u8")) {
            if (
                video.canPlayType("application/vnd.apple.mpegurl") !=
                    "probably" &&
                Hls.isSupported()
            ) {
                // Reuse existing HLS instance or create one
                if (!this.hlsInstance) {
                    this.hlsInstance = new Hls();
                }

                // Detach from previous video if any
                if (this.hlsInstance.media) {
                    try {
                        this.hlsInstance.detachMedia();
                    } catch (e) {
                        // Ignore if already detached
                    }
                }

                // Attach to new video
                this.hlsInstance.loadSource(video.src);
                this.hlsInstance.attachMedia(video);
            }
        }

        video.onended = onComplete;
        video.play();

        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "true");
        video.setAttribute("x-webkit-airplay", "true");
        video.setAttribute("x5-video-player-type", "h5");
        if (!onComplete) video.setAttribute("loop", "true");
        video.preload = "metadata";

        return video;
    };

    #render() {
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera);
    }
}

const lisa = new LisaGL();
export { lisa, LISA_BASE_PATH };
